import * as React from 'react';
import { useState, useEffect, useReducer } from 'react';
import Onboard from 'bnc-onboard';
import {
  API as OnboardApi,
  Wallet,
  Initialization,
} from 'bnc-onboard/dist/src/interfaces';
import { providers, ethers, BigNumber, utils, Signer } from 'ethers';
import { BigNumber as BN } from 'bignumber.js';
import { formatEther } from '@ethersproject/units';
import { Erc20DetailedFactory } from '../interfaces/Erc20DetailedFactory';
import { Erc20Detailed } from '../interfaces/Erc20Detailed';
import { TokenInfo, Tokens, tokensReducer } from './tokensReducer';

export type OnboardConfig = Partial<Omit<Initialization, 'networkId'>>;

type EthGasStationSettings = 'fast' | 'fastest' | 'safeLow' | 'average';
type EtherchainGasSettings =
  | 'safeLow'
  | 'standard'
  | 'fast'
  | 'fastest'
  | 'currentBaseFee'
  | 'recommendedBaseFee';
type EthGasnowSettings = 'fast' | 'standard' | 'slow';

type TokenConfig = {
  address: string;
  name?: string;
  symbol?: string;
  imageUri?: string;
  native: boolean;
  decimals: number;
};

type TokensToWatch = {
  [networkId: number]: TokenConfig[];
};

type BridgeConfig = {
  chainId: number;
  networkId: number;
  name: string;
  bridgeAddress: string;
  erc20HandlerAddress: string;
  rpcUrl: string;
  type: "Ethereum" | "Substrate";
  tokens: TokenConfig[];
  nativeTokenSymbol: string;
  nativeTokenDecimals: number;

  amplController: string;
  amplApprovalContract: string;

  //This should be the full path to display a tx hash, without the trailing slash, ie. https://etherscan.io/tx
  blockExplorer: string;
  defaultGasPrice?: number;
  testnet?: boolean;
};

type Web3ContextProps = {
  cacheWalletSelection?: boolean;
  checkNetwork?: boolean;
  children: React.ReactNode;
  ethGasStationApiKey?: string;
  gasPricePollingInterval?: number; //Seconds between gas price polls. Defaults to 0 - Disabled
  gasPriceSetting?:
  | EthGasStationSettings
  | EtherchainGasSettings
  | EthGasnowSettings;
  networkIds?: number[];
  onboardConfig?: OnboardConfig;
  spenderAddress?: string;
  tokensToWatch?: TokensToWatch; // Network-keyed collection of token addresses to watch
  ethGasPriceFromGasnow?: Boolean;
  useGasPricePoller?: Boolean;
  bridgeList?: BridgeConfig[];
  loopNativeTokenInterval?: number;
};

type Web3Context = {
  address?: string;
  ethBalance?: number;
  gasPrice: number;
  isReady: boolean;
  isMobile: boolean;
  network?: number;
  onboard?: OnboardApi;
  provider?: providers.Web3Provider;
  wallet?: Wallet;
  tokens: Tokens;
  walletConnecting?: boolean;
  timer?: NodeJS.Timer | null,
  checkIsReady(): Promise<boolean>;
  refreshGasPrice(): Promise<Number>;
  resetOnboard(): void;
  signMessage(message: string): Promise<string>;
  updateCurrentTokenBalance(address: string): void;
};

const Web3Context = React.createContext<Web3Context | undefined>(undefined);

const Web3Provider = ({
  children,
  onboardConfig,
  networkIds,
  ethGasStationApiKey,
  gasPricePollingInterval = 0,
  gasPriceSetting = 'fast',
  tokensToWatch,
  spenderAddress,
  cacheWalletSelection = true,
  checkNetwork = (networkIds && networkIds.length > 0) || false,
  ethGasPriceFromGasnow = true,
  useGasPricePoller = false,
  bridgeList,
  loopNativeTokenInterval = 6000,
}: Web3ContextProps) => {
  const [address, setAddress] = useState<string | undefined>(undefined);
  const [provider, setProvider] = useState<providers.Web3Provider | undefined>(
    undefined
  );
  const [network, setNetwork] = useState<number | undefined>(undefined);
  const [ethBalance, setEthBalance] = useState<number | undefined>(undefined);
  const [wallet, setWallet] = useState<Wallet | undefined>(undefined);
  const [onboard, setOnboard] = useState<OnboardApi | undefined>(undefined);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [tokens, tokensDispatch] = useReducer(tokensReducer, {});
  const [gasPrice, setGasPrice] = useState(0);
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [tokenContract, setTokenContract] = useState<Erc20Detailed | undefined>(undefined);
  const [timer, setTimer] = useState<NodeJS.Timer | null>(null);

  // Initialize OnboardJS
  const initializeOnboard = async () => {
    const checks = [{ checkName: 'accounts' }, { checkName: 'connect' }];
    if (networkIds && checkNetwork) {
      checks.push({ checkName: 'network' });
    }

    try {
      const onboard = Onboard({
        ...onboardConfig,
        networkId: networkIds ? networkIds[0] : 1, //Default to mainnet
        walletCheck: checks,
        subscriptions: {
          address: (address) => {
            setAddress(address);
            // checkIsReady();
            onboardConfig?.subscriptions?.address &&
              onboardConfig?.subscriptions?.address(address);
          },
          wallet: (wallet) => {
            if (wallet.provider) {
              // wallet.name &&
              //   cacheWalletSelection &&
              //   sessionStorage.setItem('onboard.selectedWallet', wallet.name);
              setWallet(wallet);
              setProvider(
                new ethers.providers.Web3Provider(wallet.provider, 'any')
              );
            } else {
              setWallet(undefined);
            }
            onboardConfig?.subscriptions?.wallet &&
              onboardConfig.subscriptions.wallet(wallet);
          },
          network: (network) => {
            const ethereum = (window as any).ethereum;
            ethereum.removeAllListeners(['networkChanged']);

            if (!networkIds || networkIds.includes(network)) {
              onboard.config({ networkId: network });
            }
            if (timer) {
              clearInterval(timer);
            }
            wallet &&
              wallet.provider &&
              setProvider(
                new ethers.providers.Web3Provider(wallet.provider, 'any')
              );
            setNetwork(network);

            console.log('Switch Network:', network);
            onboardConfig?.subscriptions?.network &&
              onboardConfig.subscriptions.network(network);
          },
          balance: (balance) => {
            console.log('onboard balance callback')
            try {
              const bal = Number(formatEther(balance));
              !isNaN(bal) ? setEthBalance(bal) : setEthBalance(0);
            } catch (error) {
              setEthBalance(0);
            }
            onboardConfig?.subscriptions?.balance &&
              onboardConfig.subscriptions.balance(balance);
          },
        },
      });

      setOnboard(onboard);
    } catch (error) {
      console.log('Error initializing onboard');
      console.log(error);
    }
  };

  useEffect(() => {
    console.log('initializeOnboard')
    initializeOnboard();
  }, []);

  useEffect(() => {
    if (onboard) {
      selectWallet();
    }
  }, [onboard]);

  const checkIsReady = async () => {
    console.log('exe checkIsReady');
    console.log('onboard', onboard)
    const isReady = await onboard?.walletCheck();
    console.log('isReady: ', isReady);
    if (isReady) {
      const currentState = onboard?.getState();
      if (currentState?.wallet && currentState?.wallet.name) {
        cacheWalletSelection &&
          sessionStorage.setItem(
            'onboard.selectedWallet',
            currentState?.wallet.name
          );
      }

      setWalletConnecting(false);
    }
    setIsReady(!!isReady);
    return !!isReady;
  };

  const selectWallet = async () => {
    setWalletConnecting(true);
    const savedWallet = sessionStorage.getItem('onboard.selectedWallet');
    let selected;
    if (savedWallet) {
      selected = await onboard?.walletSelect(savedWallet);
    } else {
      selected = await onboard?.walletSelect();
    }

    selected && (await checkIsReady());
  };

  const resetOnboard = async () => {
    sessionStorage.removeItem('onboard.selectedWallet');
    setIsReady(false);
    onboard?.walletReset();
    await selectWallet();
  };

  // Gas Price poller
  useEffect(() => {
    let poller: NodeJS.Timeout;
    if (useGasPricePoller) {
      if (network === 1 && gasPricePollingInterval > 0) {
        refreshGasPrice();
        poller = setInterval(refreshGasPrice, gasPricePollingInterval * 1000);
      } else {
        setGasPrice(10);
      }
    }

    return () => {
      if (poller) {
        clearInterval(poller);
      }
    };
  }, [network]);

  // Token balance and allowance listener
  // TODO: Allowance check not needed unless target is specificed
  useEffect(() => {
    const networkTokens =
      (tokensToWatch && network && tokensToWatch[network]) || [];
    if (provider && address && networkTokens.length > 0) {
      networkTokens.forEach(async (token) => {
        if (token.native) {
          let decimals = 18;
          if (!token.decimals) {
            console.log(
              'There was an error getting the native token decimals.'
            );
          } else {
            decimals = token.decimals;
          }
          const newNativeTokenInfo: TokenInfo = {
            decimals,
            native: true,
            balance: ethBalance || 0,
            balanceBN: new BN(ethBalance || 0),
            imageUri: token.imageUri,
            name: token.name,
            symbol: token.symbol,
            spenderAllowance: 0,
          };

          tokensDispatch({
            type: 'addToken',
            payload: { id: token.address, token: newNativeTokenInfo },
          });
          return;
        }

        const newTokenInfo: TokenInfo = {
          native: false,
          decimals: token.decimals,
          balance: 0,
          balanceBN: new BN(0),
          imageUri: token.imageUri,
          name: token.name,
          symbol: token.symbol,
          spenderAllowance: 0,
        };

        console.log(
          'decimals: ',
          newTokenInfo.decimals,
          'for',
          newTokenInfo.symbol
        );

        tokensDispatch({
          type: 'addToken',
          payload: { id: token.address, token: newTokenInfo },
        });
      });
    }
    return () => {
      tokensDispatch({ type: 'resetTokens' });
    };
  }, [network, provider, address]);

  const checkBalanceAndAllowance = async (token: Erc20Detailed) => {
    if (address) {
      const bal = await token.balanceOf(address);
      let decimals = 18;
      if (tokens[token.address]) {
        decimals = tokens[token.address].decimals;
      } else {
        decimals = await token.decimals();
      }
      const balance = Number(utils.formatUnits(bal, decimals));
      console.log('balance', balance)
      const balanceBN = new BN(bal.toString()).shiftedBy(-decimals);
      var spenderAllowance = 0;
      if (spenderAddress) {
        spenderAllowance = Number(
          utils.formatUnits(
            BigNumber.from(await token.balanceOf(address)),
            decimals
          )
        );
      }

      tokensDispatch({
        type: 'updateTokenBalanceAllowance',
        payload: {
          id: token.address,
          spenderAllowance: spenderAllowance,
          balance: balance,
          balanceBN,
        },
      });
    }
  };

  // let tokenContract: Erc20Detailed;

  const updateCurrentTokenBalance = async (currentTokenAddress: string) => {
    if (tokenContract) {
      tokenContract.removeAllListeners();
    }

    if (timer) {
      clearInterval(timer);
    }

    if (provider && address && network) {
      let signerOrPrivider: Signer | providers.Provider = provider.getSigner();
      if (bridgeList) {
        const bridage = bridgeList.find(b => b.networkId === network);
        if (bridage) {
          console.log('rpc url: ', bridage.rpcUrl)
          signerOrPrivider = new ethers.providers.JsonRpcProvider(bridage.rpcUrl)
        }
      }

      const networkTokens = (tokensToWatch && tokensToWatch[network]) || [];

      const token = networkTokens.find(t => t.address === currentTokenAddress);

      if (token && token.native) {
        setTimer(setInterval(async () => {
          const nativeBalance = await signerOrPrivider.getBalance(address);
          const bal = Number(formatEther(nativeBalance))
          console.log(`get ${token.symbol} balance: ${bal} on ${network}`)
          !isNaN(bal) ? setEthBalance(bal) : setEthBalance(0);
          const newNativeTokenInfo: TokenInfo = {
            decimals: token.decimals,
            native: true,
            balance: bal,
            balanceBN: new BN(bal),
            imageUri: token.imageUri,
            name: token.name,
            symbol: token.symbol,
            spenderAllowance: 0,
          };

          tokensDispatch({
            type: 'addToken',
            payload: { id: token.address, token: newNativeTokenInfo },
          });
        }, loopNativeTokenInterval))

        return;
      }

      const c = Erc20DetailedFactory.connect(currentTokenAddress, signerOrPrivider)
      checkBalanceAndAllowance(c)
      setTokenContract(c)

      // This filter is intentionally left quite loose.
      const filterTokenApproval = c.filters.Approval(
        address,
        null,
        null
      );
      const filterTokenTransferFrom = c.filters.Transfer(
        address,
        null,
        null
      );
      const filterTokenTransferTo = c.filters.Transfer(
        null,
        address,
        null
      );

      c.on(filterTokenApproval, () =>
        checkBalanceAndAllowance(c)
      );
      c.on(filterTokenTransferFrom, () =>
        checkBalanceAndAllowance(c)
      );
      c.on(filterTokenTransferTo, () =>
        checkBalanceAndAllowance(c)
      );
    }
  }

  const signMessage = async (message: string) => {
    if (!provider) return Promise.reject('The provider is not yet initialized');

    const data = ethers.utils.toUtf8Bytes(message);
    const signer = await provider.getSigner();
    const addr = await signer.getAddress();
    const sig = await provider.send('personal_sign', [
      ethers.utils.hexlify(data),
      addr.toLowerCase(),
    ]);
    return sig;
  };

  const refreshGasPrice = async () => {
    console.log('refresh gas price');
    if (network === 1) {
      let gasPrice = 65;
      let defaultGasPrice;
      try {
        if (provider) {
          const res = await provider?.getGasPrice();
          if (res) {
            defaultGasPrice = Number(utils.formatUnits(res, 'gwei'));
          }
          console.log(
            'Default gas price:',
            defaultGasPrice?.toFixed(2),
            'GWei'
          );
        }

        if (ethGasPriceFromGasnow) {
          const ethGasnowResponse = await (
            await fetch(
              'https://www.gasnow.org/api/v3/gas/price?utm_source=meter'
            )
          ).json();

          const gasnowGasPrice = ethGasnowResponse.data[gasPriceSetting] / 1e9;

          gasPrice = gasnowGasPrice;
          console.log('GasNow gas price: ', gasnowGasPrice.toFixed(2), 'GWei');
        } else if (ethGasStationApiKey) {
          const ethGasStationResponse = await (
            await fetch(
              `https://ethgasstation.info/api/ethgasAPI.json?api-key=${ethGasStationApiKey}`
            )
          ).json();
          gasPrice = ethGasStationResponse[gasPriceSetting] / 10;
          console.log('ethGasStation gas price:', gasPrice.toFixed(2), 'GWei');
        } else {
          const etherchainResponse = await (
            await fetch('https://www.etherchain.org/api/gasPriceOracle')
          ).json();
          gasPrice = Number(etherchainResponse[gasPriceSetting]);
          console.log('etherchain gas price:', gasPrice.toFixed(2), 'GWei');
        }
      } catch (error) {
        console.log('Error during getting gas price:', error);
      } finally {
        const newGasPrice = !isNaN(Number(gasPrice))
          ? Number(gasPrice)
          : defaultGasPrice
            ? defaultGasPrice
            : 65;
        setGasPrice(newGasPrice);
        return newGasPrice;
      }
    } else {
      return 10;
    }
  };

  const onboardState = onboard?.getState();

  return (
    <Web3Context.Provider
      value={{
        address: address,
        provider,
        network: network,
        ethBalance: ethBalance,
        wallet: wallet,
        onboard: onboard,
        isReady: isReady,
        checkIsReady,
        resetOnboard,
        gasPrice,
        refreshGasPrice,
        isMobile: !!onboardState?.mobileDevice,
        tokens: tokens,
        signMessage,
        walletConnecting,
        updateCurrentTokenBalance,
        timer
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};

const useWeb3 = () => {
  const context = React.useContext(Web3Context);
  if (context === undefined) {
    throw new Error('useOnboard must be used within a OnboardProvider');
  }
  return context;
};

export { Web3Provider, useWeb3, providers };
